import { createContext, useContext, useState, useCallback, useEffect, useMemo } from "react";
import axios from "axios";

const AuthContext = createContext(null);
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Configure axios defaults
axios.defaults.withCredentials = true;

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
}

function formatApiErrorDetail(detail) {
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail) && detail.length > 0) {
    return detail.map((e) => e.msg || e.message || String(e)).join(", ");
  }
  return "Ein Fehler ist aufgetreten";
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check if user is logged in on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const { data } = await axios.get(`${API}/auth/me`);
        setUser(data);
      } catch (err) {
        if (err?.response?.status && err.response.status !== 401) {
          console.debug("[auth] /me failed:", err.response?.status);
        }
        setUser(null);
      } finally {
        setLoading(false);
      }
    };
    checkAuth();
  }, []);

  const login = useCallback(async (email, password) => {
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      if (data.requires_2fa) {
        return { success: true, requires_2fa: true, temp_token: data.temp_token, email: data.email };
      }
      // Re-fetch /me to get full user state (profile_complete, totp_setup_required, ...)
      try {
        const { data: me } = await axios.get(`${API}/auth/me`);
        setUser(me);
      } catch (meErr) {
        console.debug("[auth] post-login /me fetch failed, using login payload as fallback:", meErr?.response?.status);
        setUser(data);
      }
      return { success: true, requires_2fa: false };
    } catch (e) {
      return { 
        success: false, 
        error: formatApiErrorDetail(e.response?.data?.detail || "Login fehlgeschlagen") 
      };
    }
  }, []);

  const verify2FA = useCallback(async (temp_token, code) => {
    try {
      await axios.post(`${API}/auth/verify-2fa`, { temp_token, code });
      // Re-fetch /me for full state
      const { data: me } = await axios.get(`${API}/auth/me`);
      setUser(me);
      return { success: true, used_backup_code: me.used_backup_code };
    } catch (e) {
      return { success: false, error: formatApiErrorDetail(e.response?.data?.detail || "2FA-Verifizierung fehlgeschlagen") };
    }
  }, []);

  const register = useCallback(async (first_name, last_name, email, password) => {
    try {
      const { data } = await axios.post(`${API}/auth/register`, { 
        first_name, 
        last_name, 
        email, 
        password 
      });
      setUser(data);
      return { success: true };
    } catch (e) {
      return { 
        success: false, 
        error: formatApiErrorDetail(e.response?.data?.detail || "Registrierung fehlgeschlagen") 
      };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`, {});
    } catch (err) {
      console.debug("[auth] logout API failed (logging out locally anyway):", err?.response?.status);
    }
    setUser(null);
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`);
      setUser(data);
    } catch (err) {
      console.debug("[auth] refreshUser failed:", err?.response?.status);
    }
  }, []);

  const isAuthenticated = !!user;
  const isApproved = user?.status === "approved";
  const actions = useMemo(
    () => ({ login, verify2FA, register, logout, refreshUser }),
    [login, verify2FA, register, logout, refreshUser]
  );
  const value = useMemo(
    () => ({ user, loading, isAuthenticated, isApproved, ...actions }),
    [user, loading, isAuthenticated, isApproved, actions]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
